import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShoppingBag, FiSearch, FiFilter, FiX, FiHeart, FiMessageCircle, FiMapPin, FiClock, FiUser, FiStar, FiEye, FiPlus, FiCheck } = FiIcons;

const Marketplace = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);

  // Sample marketplace items
  const marketplaceItems = [
    {
      id: 1,
      title: 'Advanced Mathematics Study Guide',
      description: 'Comprehensive guide covering calculus, algebra, and trigonometry with solved examples.',
      price: 25.00,
      category: 'study-materials',
      condition: 'new',
      seller: {
        name: 'Aminata Kargbo',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
        rating: 4.8,
        location: 'Freetown, Sierra Leone'
      },
      images: ['https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400&h=300&fit=crop'],
      posted: '2 hours ago',
      views: 45,
      likes: 12,
      tags: ['mathematics', 'calculus', 'algebra']
    },
    {
      id: 2,
      title: 'Scientific Calculator TI-84',
      description: 'Barely used scientific calculator, perfect for advanced mathematics and science courses.',
      price: 45.00,
      category: 'electronics',
      condition: 'used',
      seller: {
        name: 'Omar Jallow',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
        rating: 4.9,
        location: 'Banjul, The Gambia'
      },
      images: ['https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=400&h=300&fit=crop'],
      posted: '1 day ago',
      views: 78,
      likes: 23,
      tags: ['calculator', 'mathematics', 'science']
    },
    {
      id: 3,
      title: 'English Literature Collection',
      description: 'Set of 10 classic literature books including Shakespeare, Dickens, and African authors.',
      price: 35.00,
      category: 'books',
      condition: 'good',
      seller: {
        name: 'Fatou Conteh',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
        rating: 4.7,
        location: 'Bo, Sierra Leone'
      },
      images: ['https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop'],
      posted: '3 days ago',
      views: 92,
      likes: 18,
      tags: ['literature', 'books', 'english']
    },
    {
      id: 4,
      title: 'Study Desk with Lamp',
      description: 'Wooden study desk in excellent condition with built-in LED lamp. Perfect for students.',
      price: 120.00,
      category: 'furniture',
      condition: 'excellent',
      seller: {
        name: 'Isatu Bangura',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
        rating: 4.9,
        location: 'Freetown, Sierra Leone'
      },
      images: ['https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop'],
      posted: '5 days ago',
      views: 156,
      likes: 34,
      tags: ['furniture', 'desk', 'study']
    },
    {
      id: 5,
      title: 'Physics Lab Equipment Set',
      description: 'Complete physics lab equipment including microscope, test tubes, and measurement tools.',
      price: 80.00,
      category: 'lab-equipment',
      condition: 'good',
      seller: {
        name: 'Lamin Jobe',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
        rating: 4.6,
        location: 'Serrekunda, The Gambia'
      },
      images: ['https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&h=300&fit=crop'],
      posted: '1 week ago',
      views: 67,
      likes: 15,
      tags: ['physics', 'lab', 'equipment']
    },
    {
      id: 6,
      title: 'Tutoring Services - Mathematics',
      description: 'Experienced mathematics tutor offering one-on-one and group tutoring sessions.',
      price: 15.00,
      category: 'services',
      condition: 'new',
      seller: {
        name: 'Mariama Sesay',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
        rating: 5.0,
        location: 'Online'
      },
      images: ['https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop'],
      posted: '2 weeks ago',
      views: 234,
      likes: 56,
      tags: ['tutoring', 'mathematics', 'education']
    }
  ];

  const categories = [
    { id: 'all', name: 'All Items', count: marketplaceItems.length },
    { id: 'books', name: 'Books', count: marketplaceItems.filter(item => item.category === 'books').length },
    { id: 'electronics', name: 'Electronics', count: marketplaceItems.filter(item => item.category === 'electronics').length },
    { id: 'study-materials', name: 'Study Materials', count: marketplaceItems.filter(item => item.category === 'study-materials').length },
    { id: 'furniture', name: 'Furniture', count: marketplaceItems.filter(item => item.category === 'furniture').length },
    { id: 'lab-equipment', name: 'Lab Equipment', count: marketplaceItems.filter(item => item.category === 'lab-equipment').length },
    { id: 'services', name: 'Services', count: marketplaceItems.filter(item => item.category === 'services').length }
  ];

  // Filter items based on search term and category
  useEffect(() => {
    const filtered = marketplaceItems.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    setFilteredItems(filtered);
  }, [searchTerm, selectedCategory]);

  const handleContactSeller = (seller) => {
    addNotification({
      type: 'info',
      title: 'Contact Seller',
      message: `Opening chat with ${seller.name}...`,
      action: '/chat'
    });
  };

  const handleLikeItem = (itemId) => {
    addNotification({
      type: 'success',
      title: 'Item Liked',
      message: 'Item added to your favorites!'
    });
  };

  const handleBuyItem = (item) => {
    addNotification({
      type: 'success',
      title: 'Purchase Initiated',
      message: `Contacting ${item.seller.name} about "${item.title}"...`,
      action: '/chat'
    });
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'new': return 'bg-green-100 text-green-800';
      case 'excellent': return 'bg-blue-100 text-blue-800';
      case 'good': return 'bg-yellow-100 text-yellow-800';
      case 'fair': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'books': return FiShoppingBag;
      case 'electronics': return FiShoppingBag;
      case 'study-materials': return FiShoppingBag;
      case 'furniture': return FiShoppingBag;
      case 'lab-equipment': return FiShoppingBag;
      case 'services': return FiUser;
      default: return FiShoppingBag;
    }
  };

  return (
    <div className="space-y-6 px-4 py-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiShoppingBag} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Marketplace</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Buy and sell educational materials, equipment, and services within the ILM Global community.
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search items, categories, or sellers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filter Button (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <SafeIcon icon={FiFilter} className="w-5 h-5" />
            <span>Filter</span>
          </button>

          {/* Post Item Button */}
          <button
            onClick={() => addNotification({
              type: 'info',
              title: 'Post Item',
              message: 'Item posting feature coming soon!'
            })}
            className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Post Item</span>
          </button>
        </div>

        {/* Categories (Desktop) */}
        <div className="hidden lg:flex flex-wrap gap-2 mt-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedCategory === category.id
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 pt-4 border-t border-gray-200 lg:hidden"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === category.id
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            {/* Item Image */}
            <div className="relative h-48 bg-gray-200">
              <img
                src={item.images[0]}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(item.condition)}`}>
                  {item.condition}
                </span>
              </div>
              <div className="absolute top-3 right-3 flex space-x-2">
                <button
                  onClick={() => handleLikeItem(item.id)}
                  className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all"
                >
                  <SafeIcon icon={FiHeart} className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Item Details */}
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1 line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-2xl font-bold text-primary-600">
                    ${item.price.toFixed(2)}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {item.description}
              </p>

              {/* Seller Info */}
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src={item.seller.avatar}
                  alt={item.seller.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-800 text-sm">{item.seller.name}</p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <div className="flex items-center">
                      <SafeIcon icon={FiStar} className="w-3 h-3 mr-1 text-yellow-500" />
                      <span>{item.seller.rating}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center">
                      <SafeIcon icon={FiMapPin} className="w-3 h-3 mr-1" />
                      <span>{item.seller.location}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Item Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center">
                    <SafeIcon icon={FiEye} className="w-3 h-3 mr-1" />
                    <span>{item.views}</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiHeart} className="w-3 h-3 mr-1" />
                    <span>{item.likes}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                  <span>{item.posted}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-4">
                {item.tags.slice(0, 3).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBuyItem(item)}
                  className="flex-1 bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors text-sm"
                >
                  Buy Now
                </button>
                <button
                  onClick={() => handleContactSeller(item.seller)}
                  className="flex items-center justify-center bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* No Results */}
      {filteredItems.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No items found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search terms or browse different categories.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Clear Filters
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default Marketplace;